import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import { useGet } from "../../hooks/useGet";
import { ApiResponse, MoData } from "../../utils/types";
import { useFilterContext } from "../../context/FilterContext";
import TablePagination from "../TablePagination";
import ExportButton from "./ExportButton";
import MessageDetailsModal from "./MessageDetailsModal";

const MoTable: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedRow, setSelectedRow] = useState<MoData | null>(null);

  const {
    appliedNetwork,
    appliedPhoneNumber,
    appliedStartDate,
    appliedEndDate,
  } = useFilterContext();

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedNetwork) params.append("network", appliedNetwork);
    if (appliedPhoneNumber) params.append("msisdn", appliedPhoneNumber);
    if (appliedStartDate) params.append("startDate", appliedStartDate);
    if (appliedEndDate) params.append("endDate", appliedEndDate);
    params.append("page", (pageIndex + 1).toString());
    return params.toString();
  }, [
    appliedNetwork,
    appliedPhoneNumber,
    appliedStartDate,
    appliedEndDate,
    pageIndex,
  ]);

  const { data, isLoading, error } = useGet<ApiResponse>(
    `/mo/v1/users?${queryParams}`,
    ["mo_table", queryParams]
  );

  const columns = useMemo<ColumnDef<MoData>[]>(
    () => [
      {
        accessorKey: "msisdn",
        header: "Phone Number",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "mo",
        header: "MO",
        cell: (info) => (
          <div className="truncate max-w-[150px]">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: "network",
        header: "Network",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "created_at",
        header: "Timestamp",
        cell: (info) => new Date(info.getValue() as string).toLocaleString(),
      },
    ],
    []
  );

  const table = useReactTable({
    data: error ? [] : data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: data?.last_page || 1,
    state: {
      pagination: {
        pageIndex,
        pageSize: 15,
      },
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize: 15 })
          : updater;
      setPageIndex(newState.pageIndex);
    },
  });

  if (isLoading)
    return <div className="text-center py-4 text-gray-600">Loading...</div>;


  if (!data?.data?.length || error)
    return (
      <div className="text-center py-4 text-gray-600 font-medium">
        No mobile originated data yet
      </div>
    );

  return (
    <div className="p-4 sm:p-6 max-w-full sm:max-w-7xl mx-auto">
      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
        <ExportButton data={data} />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-800">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-white uppercase tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`transition-colors duration-150 ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-blue-50`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-medium"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination
          table={table}
          data={data}
          setPageIndex={setPageIndex}
        />
      </div>

      {selectedRow && (
        <MessageDetailsModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
        />
      )}
    </div>
  );
};

export default MoTable;
